import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

// Shared button styles
const buttonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.size === 'large' ? '0.75rem 1.5rem' : 
                      props.size === 'small' ? '0.375rem 0.75rem' : 
                      '0.5rem 1rem'};
  font-size: ${props => props.size === 'large' ? '1.125rem' : 
                       props.size === 'small' ? '0.875rem' : 
                       '1rem'};
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
  text-decoration: none;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  border-radius: 8px;
  transition: all 0.3s ease;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  /* Variant styles */
  background-color: ${props => 
    props.variant === 'primary' ? 'var(--primary)' :
    props.variant === 'secondary' ? 'var(--secondary)' :
    props.variant === 'outline' ? 'transparent' :
    props.variant === 'link' ? 'transparent' :
    'var(--primary)'};
  
  color: ${props => 
    props.variant === 'outline' || props.variant === 'link' ? 
      (props.variant === 'outline' ? 'var(--primary)' : 'var(--primary)') : 
      'var(--white)'};
  
  border-color: ${props => 
    props.variant === 'primary' ? 'var(--primary)' :
    props.variant === 'secondary' ? 'var(--secondary)' :
    props.variant === 'outline' ? 'var(--primary)' :
    props.variant === 'link' ? 'transparent' :
    'var(--primary)'};
  
  &:hover, &:focus {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    
    background-color: ${props => 
      props.variant === 'primary' ? 'var(--primary-dark)' :
      props.variant === 'secondary' ? 'var(--secondary-dark)' :
      props.variant === 'outline' ? 'var(--primary)' :
      props.variant === 'link' ? 'transparent' :
      'var(--primary-dark)'};
    
    color: ${props => 
      props.variant === 'link' ? 'var(--primary-dark)' : 
      props.variant === 'outline' && !props.disabled ? 'var(--white)' : 
      'var(--white)'};
    
    border-color: ${props => 
      props.variant === 'primary' ? 'var(--primary-dark)' :
      props.variant === 'secondary' ? 'var(--secondary-dark)' :
      props.variant === 'outline' ? 'var(--primary-dark)' :
      props.variant === 'link' ? 'transparent' :
      'var(--primary-dark)'};
  }
  
  &:disabled {
    opacity: 0.65;
    pointer-events: none;
  }
  
  /* Link variant specific styles */
  text-decoration: ${props => props.variant === 'link' ? 'underline' : 'none'};
  padding: ${props => props.variant === 'link' ? '0' : ''};
  
  /* Icon spacing */
  svg, i {
    margin-right: ${props => props.children ? '0.5rem' : '0'};
  }
`;

// Create the styled components using the shared styles
const StyledButton = styled.button`${buttonStyles}`;
const StyledLink = styled(Link)`${buttonStyles}`;

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'medium', 
  fullWidth = false,
  icon,
  to, 
  onClick, 
  type = 'button',
  disabled = false,
  className = '',
  ...rest 
}) => {
  // If "to" prop is provided, render a Link component
  if (to) {
    return (
      <StyledLink 
        to={to} 
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        className={className}
        {...rest}
      >
        {icon && icon}
        {children}
      </StyledLink>
    );
  }
  
  // Otherwise render a button
  return (
    <StyledButton 
      type={type} 
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      onClick={onClick}
      disabled={disabled}
      className={className}
      {...rest}
    >
      {icon && icon}
      {children}
    </StyledButton>
  );
};

export default Button;
